import { useCallback, useEffect, useMemo, useState } from 'react';
import { Timestamp } from '@firebase/firestore';
import { commentsApi, type CreateCommentData, type CommentsCursor } from '../api/comments';
import { useAuthStore } from '../store/authStore';
import { useAuthGateStore } from '../store/authGateStore';
import { useUIStore } from '../store/uiStore';
import { usePostsStore } from '../store/postsStore';
import { anonWritesBlocked } from '../constants/auth';
import { appAlert } from '../utils/appAlert';
import { scheduleDeferredCommit } from '../utils/deferredCommit';
import { getErrorMessage } from '../utils/errorHandler';
import type { Comment } from '../types';

// A comment shown before its write has confirmed. `pending` comments are
// never persisted anywhere — the id is client-generated and thrown away
// (replaced by the server copy, or removed) the moment the write settles, so
// nothing else in the app needs to know about this shape.
export type CommentWithPending = Comment & { pending?: boolean };

function tempCommentId(): string {
  return `pending-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

/**
 * One post's comment thread: load, paginate, send, edit, delete, plus the
 * auth/anon gating a write needs. Shared by the two presentations —
 * CommentsSheet below the desktop shell, InlineComments on it — so the two
 * can never drift on gating or counter bookkeeping.
 *
 * `active` defers the initial load until the thread is actually on screen
 * (the sheet passes its `visible` flag).
 */
export function useCommentThread(postId: string, active = true) {
  const user = useAuthStore((s) => s.user);
  const incrementCommentCount = usePostsStore((s) => s.incrementCommentCount);
  const decrementCommentCount = usePostsStore((s) => s.decrementCommentCount);
  const markCommentPendingHidden = usePostsStore((s) => s.markCommentPendingHidden);
  const clearCommentPendingHidden = usePostsStore((s) => s.clearCommentPendingHidden);

  const [comments, setComments] = useState<CommentWithPending[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSending, setIsSending] = useState(false);
  const [commentText, setCommentText] = useState('');
  const [editingCommentId, setEditingCommentId] = useState<string | null>(null);
  const [lastDoc, setLastDoc] = useState<CommentsCursor | null>(null);
  const [hasMore, setHasMore] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  // Undo toast for delete (panel-system.md Phase 4, rule 6), not a confirm:
  // hidden the moment delete is pressed, the real API delete only goes out
  // six seconds later unless Undo cancels it (src/utils/deferredCommit.ts).
  // `comments` itself isn't spliced at hide time — only filtered by this set
  // at the point this hook returns its list — so Undo has the original row
  // to restore with no server round trip.
  const [hiddenCommentIds, setHiddenCommentIds] = useState<Record<string, true>>({});

  const loadComments = useCallback(async () => {
    setIsLoading(true);
    setLoadError(null);
    try {
      const result = await commentsApi.getComments(postId);
      setComments(result.comments);
      setLastDoc(result.lastDoc);
      setHasMore(result.comments.length >= 20);
    } catch (err) {
      setLoadError(getErrorMessage(err));
    } finally {
      setIsLoading(false);
    }
  }, [postId]);

  useEffect(() => {
    if (active) loadComments();
  }, [active, loadComments]);

  const loadMoreComments = useCallback(async () => {
    if (!hasMore || !lastDoc) return;
    try {
      const result = await commentsApi.getComments(postId, lastDoc);
      setComments((prev) => [...prev, ...result.comments]);
      setLastDoc(result.lastDoc);
      setHasMore(result.comments.length >= 20);
    } catch (err) {
      // Surface the failure inline so the user can retry; the existing
      // comments stay rendered (we don't reset them).
      setLoadError(getErrorMessage(err));
    }
  }, [hasMore, lastDoc, postId]);

  const handleSend = useCallback(async () => {
    if (!commentText.trim() || isSending) return;
    // Signed-out visitors can read public threads (share links), but posting
    // needs an identity — send them through the auth gate instead of
    // silently dropping the tap. The draft stays in the input.
    if (!user) {
      useAuthGateStore.getState().open(() => {}, { message: 'Sign in to comment' });
      return;
    }
    // Anonymous Fire Scout while writes are flag-disabled: commenting needs
    // a durable account. Draft stays in the input. See ANON_WRITES_ENABLED.
    if (anonWritesBlocked(user)) {
      useUIStore.getState().openSheet('upgradeAccount');
      return;
    }

    setIsSending(true);
    const text = commentText.trim();
    setCommentText('');

    if (editingCommentId) {
      try {
        await commentsApi.updateComment(postId, editingCommentId, text);
        setComments((prev) =>
          prev.map((c) => (c.id === editingCommentId ? { ...c, text, updatedAt: {} as never } : c))
        );
        setEditingCommentId(null);
      } catch (err) {
        console.error('Failed to update comment:', err);
        setCommentText(text);
        appAlert('Error', 'Failed to update comment. Please try again.');
      }
    } else {
      // Optimistic insert — same idea as postsStore's like/unlike: show the
      // result immediately, reconcile once the write settles. The comment
      // list here is local state fed by a one-time fetch (not a live
      // listener — see useCommentThread's own loadComments), so there is no
      // subscription that could double-insert this row; the temp id only
      // has to survive until the reconcile below replaces or removes it.
      const tempId = tempCommentId();
      const optimistic: CommentWithPending = {
        id: tempId,
        authorId: user.id,
        authorName: `${user.firstName} ${user.lastName}`,
        text,
        createdAt: Timestamp.now(),
        pending: true,
      };
      setComments((prev) => [...prev, optimistic]);
      try {
        const data: CreateCommentData = {
          postId,
          authorId: user.id,
          authorName: `${user.firstName} ${user.lastName}`,
          text,
        };
        const newComment = await commentsApi.createComment(data);
        setComments((prev) => prev.map((c) => (c.id === tempId ? newComment : c)));
        incrementCommentCount(postId);
      } catch {
        setComments((prev) => prev.filter((c) => c.id !== tempId));
        setCommentText(text);
        useUIStore.getState().showToast({ message: "Couldn't post your comment" });
      }
    }
    setIsSending(false);
  }, [commentText, user, isSending, postId, editingCommentId, incrementCommentCount]);

  const handleStartEdit = useCallback((comment: Comment) => {
    setEditingCommentId(comment.id);
    setCommentText(comment.text);
  }, []);

  const handleCancelEdit = useCallback(() => {
    setEditingCommentId(null);
    setCommentText('');
  }, []);

  const handleDelete = useCallback(
    (commentId: string) => {
      // Same anon gate as handleSend: deleting is a write too.
      if (anonWritesBlocked(user)) {
        useUIStore.getState().openSheet('upgradeAccount');
        return;
      }
      scheduleDeferredCommit({
        key: `comment-${postId}-${commentId}`,
        message: 'Comment deleted',
        commit: async () => {
          await commentsApi.deleteComment(postId, commentId);
          setComments((prev) => prev.filter((c) => c.id !== commentId));
          decrementCommentCount(postId);
          setHiddenCommentIds((prev) => {
            if (!(commentId in prev)) return prev;
            const next = { ...prev };
            delete next[commentId];
            return next;
          });
          clearCommentPendingHidden(postId, commentId);
        },
        onHide: () => {
          setHiddenCommentIds((prev) => ({ ...prev, [commentId]: true }));
          markCommentPendingHidden(postId, commentId);
        },
        onUndo: () => {
          setHiddenCommentIds((prev) => {
            if (!(commentId in prev)) return prev;
            const next = { ...prev };
            delete next[commentId];
            return next;
          });
          clearCommentPendingHidden(postId, commentId);
        },
      });
    },
    [postId, user, decrementCommentCount, markCommentPendingHidden, clearCommentPendingHidden]
  );

  const visibleComments = useMemo(
    () => comments.filter((c) => !hiddenCommentIds[c.id]),
    [comments, hiddenCommentIds]
  );

  return {
    comments: visibleComments,
    isLoading,
    isSending,
    loadError,
    commentText,
    setCommentText,
    editingCommentId,
    currentUserId: user?.id ?? '',
    loadComments,
    loadMoreComments,
    handleSend,
    handleStartEdit,
    handleCancelEdit,
    handleDelete,
  };
}
